/**
 * 이상한 방 - 여럿이 판의 평면도(구획을 좌석 수만큼 굽는다)
 *
 * 근거 문서: `이상한방-컨셉시트-v0.1.md` 3절(라운드 구조) · 4.4절(원래 방)
 *            `이상한방-상태모델-v0.1.md` 3절(좌표) · 9.5절(방을 외우는 것이 스킬)
 *            로드맵 M11(각자 방 - 전원이 자기 구획을 고치고 남의 구획을 찾는다)
 *
 * ## 구획 수 = 좌석 수. 폴백이 없다
 *
 * M11에서 역할이 사라진다 - 한 명이 고치고 나머지가 찾는 것이 아니라 **전원이 자기 구획을 고치고 남의 구획을 찾는다**.
 * 그러려면 사람 하나에 구획 하나가 정확히 대응해야 한다. 남는 구획은 "아무도 손대지 않은 방"이 되어 수색 시간을
 * 통째로 낭비시키고, 모자라는 구획은 "고칠 곳이 없는 사람"을 만든다. 둘 다 라운드가 성립하지 않는다.
 *
 * 그래서 여기 있는 표는 **좌석 수마다 격자를 고정**한다. 2·3석은 한 줄, 4석부터는 두 줄이고, 홀수(5·7)는 한 구획이
 * 두 밴드를 삼켜(`rowSpan`) 칸 수를 맞춘다. 삼키는 자리는 **가장 좁은 띠**로 골랐다 - 두 칸을 먹은 구획이 남들의 두 배가
 * 되면 그 자리에 앉은 사람만 이상한 곳을 두 배로 숨길 수 있다(넓이가 곧 숨을 곳의 수다).
 *
 * ## 난수가 없다 - 서버와 클라이언트가 각자 굽고 같은 것이 나와야 한다
 *
 * 서버는 `server/src/deps.ts`가, 클라이언트는 `main.ts`의 `planForRoomTemplate`이 각자 이 함수를 부른다. 평면도가
 * 와이어를 타고 오지 않기 때문이다 - 오는 것은 `RoomState.templateId`(`party-<n>`)뿐이고, 그 문자열 하나로 양쪽이
 * **같은 벽·같은 문·같은 창**을 세워야 소품이 벽을 뚫지 않는다. 좌석 수 말고 다른 입력이 하나라도 있으면 그 순간
 * 양쪽이 갈릴 길이 생긴다. 그래서 인자는 좌석 수 하나이고, 시드도 시계도 읽지 않는다.
 *
 * ## 좌표를 적지 않는다 - 밴드 명세를 기존 빌더에 넘긴다
 *
 * 굽는 규칙은 `bandPlan.ts`의 것을 그대로 쓴다(띠 사이 한 칸이 내벽 · 문은 맞닿은 벽 한가운데 · 창은 외벽 면에서 유도).
 * 검증도 기존 `assertValidFloorPlan` 하나를 지난다 - 여럿이 평면도만 다른 검사를 받으면 그 검사가 언젠가 뒤처진다.
 * 이 파일이 하는 일은 셋뿐이다: 좌석 수 → 격자 표를 고르고, 격자 인접 관계에서 문 목록을 뽑고, 캐시에 넣는 것.
 */

import { buildBandPlan, type BandPlanSpec, type DoorSpec, type ZoneSpec } from './bandPlan'
import { assertValidFloorPlan } from './floorPlanValidate'
import type { FloorPlan, RoomId, RoomTag } from './floorPlanModel'

/* --- 규격 ------------------------------------------------------------------------ */

/**
 * 좌석 수의 범위. 하한 2는 "혼자서는 남의 구획이 없다"이고, 상한 8은 소켓 방의 좌석 상한
 * (`net/protocol.MAX_ROOM_SEATS`)과 같은 수다. 여기서 그 상수를 import 하지 않는 이유는 방향이다 -
 * `core`가 `net`을 보면 화살표가 뒤집힌다. 대신 두 값이 갈리면 `partyPlan.test.ts`가 잡는다.
 */
export const PARTY_SEATS_MIN = 2
export const PARTY_SEATS_MAX = 8

/**
 * 구획 하나의 한 변(셀). 27 × 0.25m = **6.75m × 6.75m**.
 *
 * ## 집이 좌석 수만큼 늘어난다 (v0.1 개정 · 사용자 지시)
 *
 * 처음에는 반대였다 - 집의 크기가 고정(아파트와 같은 80×80)이고 그 집을 **좌석 수만큼 잘랐다**. 그러면
 * 둘이 앉을 때 한 사람의 구획이 2,964셀이고 여덟이면 722셀이라, **같은 게임인데 사람 수에 따라 한 사람이
 * 맡는 넓이가 네 배 벌어진다.** 넓이는 곧 숨을 곳의 수이자 걷는 거리이므로 그 차이는 그대로 난이도의 차이다
 * (그리고 생성기는 바닥이 아니라 가구 그룹이 들어갈 자리로 채우므로 넓은 구획은 채워지지도 않는다 -
 * 2석 구획 하나에 물건이 50개, 8석은 22개였다).
 *
 * 그래서 뒤집었다. **1블록의 크기가 고정이고 집이 좌석 수만큼 붙는다** - 2명이면 두 블록, 8명이면 여덟 블록이다.
 * 카메라 절두체·컷어웨이·격자는 전부 `bounds`에서 유도되므로 판마다 크기가 달라도 어긋나지 않는다
 * (`two-rooms`·`small-house`가 이미 정사각형이 아닌 평면도다). 대신 **같은 좌석 수면 언제나 같은 집**이라는
 * 규약은 그대로다 - 굽는 입력은 여전히 좌석 수 하나뿐이다.
 *
 * ## 왜 27인가
 *
 * 실제로 돌아 본 크기다. 80×80 시절의 8석 구획이 19×38 = 722셀이었고 그 안에 소품 22개가 들어갔다 -
 * 사람이 외우고 뒤질 만한 방 하나의 크기이자 싱글 아파트의 큰 방과 같은 범위다(600~1,200셀).
 * 27×27 = 729셀이 그 자리이고, 4석 판(57×57 - 직전 커밋)이 그대로 남는다는 뜻이기도 하다.
 * **한 자리에서 바꾸는 값이다** - 방이 좁거나 넓다는 판단은 실측의 문제이지 옳음의 문제가 아니다(7절 규약).
 */
export const PARTY_BLOCK_CELLS = 27

/**
 * 블록을 반으로 가른 폭(셀). 13 + 벽 1 + 13 = 27 = 한 블록이다.
 *
 * **세 칸짜리 줄과 두 칸짜리 줄이 같은 x 격자를 나눠 쓰려면** 이 폭이 필요하다(`bandPlan`의 밴드는 줄마다
 * 따로 잡을 수 없다 - 격자가 하나다). 여섯 밴드로 깔면 한 줄은 두 밴드씩 셋(27칸), 다른 줄은 세 밴드씩
 * 둘(41칸)로 같은 폭을 나눈다.
 */
const HALF_BLOCK_CELLS = 13

/**
 * 남는 줄의 블록 높이(셀). 마지막 줄이 위 줄보다 한 칸 적으면 그 줄의 블록은 **넓고 납작해진다** -
 * 넓이는 같고 모양만 다르다(55 × 13 = 715 · 41 × 18 = 738 대 27² = 729).
 *
 * 빈 칸으로 두지 않는 이유는 그림이다. 방이 없는 칸은 `render/roomShell`이 **통행 불가 셀**로 보고
 * 바닥부터 절단면까지 통째로 메우므로(`buildWallBaseFloor`), 집 한 귀퉁이에 27×27짜리 콘크리트 덩어리가
 * 서 있는 그림이 된다. 사람이 보는 것은 격자가 아니라 방이라, 남는 자리는 비우는 것이 아니라 **눕히는** 것이 맞다.
 */
const WIDE_ROW_HEIGHT_CELLS: Readonly<Record<number, number>> = {
  /** 두 밴드(55칸)를 삼킨 줄. 55 × 13 = 715셀. */
  2: 13,
  /** 세 밴드(41칸)를 삼킨 줄. 41 × 18 = 738셀. */
  3: 18,
}

/**
 * 문 폭(셀). 6 = 1.5m 아치다. 여럿이 판에서는 전원이 동시에 남의 구획을 돌아다니므로 문이 곧 병목이고,
 * 1m짜리 문에서 두 사람이 엉키면 그건 게임이 아니라 사고다. 어느 격자에서도 맞닿은 구간이 13셀 이상이라
 * 이 폭은 항상 들어간다(`bandPlan.resolveDoorway`가 넘치면 던진다).
 */
const PARTY_DOOR_WIDTH_CELLS = 6

/* --- 구획 종류 -------------------------------------------------------------------- */

/**
 * 구획 id → 성격(`RoomTag`). id가 곧 i18n 키의 꼬리다(`room.<id>` - `i18n/ko.ts`·`en.ts`가 채운다).
 * 여기 여덟 개가 여럿이 판이 쓰는 구획 전부이며, 전부 기존 사전에 이미 있는 이름이다 -
 * 여럿이만의 새 이름을 만들면 싱글에서 본 '거실'과 여럿이의 '거실'이 다른 말이 된다.
 */
const ZONE_TAGS: Readonly<Record<string, RoomTag>> = {
  living: 'living',
  kitchen: 'kitchen',
  bedroom: 'bedroom',
  study: 'living',
  bath: 'bath',
  'bedroom-main': 'bedroom',
  'bedroom-guest': 'bedroom',
  hall: 'hall',
}

/* --- 격자 표 ---------------------------------------------------------------------- */

/** 격자 한 칸(또는 여러 칸)에 앉는 구획. 좌표가 아니라 **몇 번째 띠인가**만 적는다(`bandPlan` 규약). */
type PartyZone = Readonly<{ id: RoomId; col: number; row: number; colSpan?: number; rowSpan?: number }>

type PartyLayout = Readonly<{
  /** x축 띠의 내부 폭. 전부 한 블록(`PARTY_BLOCK_CELLS`)이고, 홀수를 맞추는 복도만 절반 폭이다. */
  xBands: readonly number[]
  zBands: readonly number[]
  zones: readonly PartyZone[]
}>

/**
 * 좌석 수별 격자. **표를 눈으로 세어 구획 수를 확인할 수 있어야 한다** - 이것이 M11의 대전제이고,
 * 규칙으로 유도하면(예: `ceil(sqrt(n))`) 5·7에서 남는 칸이 생겨 폴백이 필요해진다.
 *
 * 블록은 **위 줄부터 채우고 한 줄에 셋까지**다(사용자 지시). 한 줄로 늘리면 3석이 3:1, 8석이 2:1짜리
 * 복도 같은 집이 되어 카메라가 물러나야 하고, 그러면 방 하나가 화면에서 작아진다.
 *
 * ```
 *  2 = ㅇㅇ      3 = ㅇㅇ      4 = ㅇㅇ      5 = ㅇㅇㅇ     6 = ㅇㅇㅇ     7 = ㅇㅇㅇ     8 = ㅇㅇㅇ
 *                    ㅇ            ㅇㅇ          ㅇㅇ           ㅇㅇㅇ         ㅇㅇ           ㅇㅇㅇ
 *                                                                            ㅇㅇ           ㅇㅇ
 *  57×29        57×43         57×57         85×48         85×57         85×67         85×76
 * ```
 *
 * 7석만 위에서부터 채우는 순서를 어긴다(`[3,3,1]`이 아니라 `[3,2,2]`). 셋짜리 줄 아래 하나만 남으면 그 블록이
 * 85 × 9짜리 띠가 되어 방이 아니라 통로가 되기 때문이다 - 넓이는 같아도 그건 다른 물건이다.
 *
 * 남는 줄의 블록은 넓고 납작하다(`WIDE_ROW_HEIGHT_CELLS`). 넓이는 ±3% 안에서 같고, 그것이 이 표가 지키는
 * 전부다 - 넓이가 곧 숨을 곳의 수라 한 사람만 넓으면 그 자리가 이기는 자리가 된다(테스트가 지킨다).
 */
const BLOCK = PARTY_BLOCK_CELLS
const HALF = HALF_BLOCK_CELLS
const WIDE2 = WIDE_ROW_HEIGHT_CELLS[2] ?? 0
const WIDE3 = WIDE_ROW_HEIGHT_CELLS[3] ?? 0

/** 셋짜리 줄과 둘짜리 줄이 섞이는 격자의 x축(여섯 밴드). 셋은 두 밴드씩, 둘은 세 밴드씩 가져간다. */
const SPLIT_BANDS: readonly number[] = [HALF, HALF, HALF, HALF, HALF, HALF]

const LAYOUTS: Readonly<Record<number, PartyLayout>> = {
  2: {
    xBands: [BLOCK, BLOCK],
    zBands: [BLOCK],
    zones: [
      { id: 'living', col: 0, row: 0 },
      { id: 'bedroom', col: 1, row: 0 },
    ],
  },
  3: {
    xBands: [BLOCK, BLOCK],
    zBands: [BLOCK, WIDE2],
    zones: [
      { id: 'living', col: 0, row: 0 },
      { id: 'kitchen', col: 1, row: 0 },
      { id: 'hall', col: 0, row: 1, colSpan: 2 },
    ],
  },
  4: {
    xBands: [BLOCK, BLOCK],
    zBands: [BLOCK, BLOCK],
    zones: [
      { id: 'living', col: 0, row: 0 },
      { id: 'kitchen', col: 1, row: 0 },
      { id: 'bedroom', col: 0, row: 1 },
      { id: 'study', col: 1, row: 1 },
    ],
  },
  5: {
    xBands: SPLIT_BANDS,
    zBands: [BLOCK, WIDE3],
    zones: [
      { id: 'living', col: 0, row: 0, colSpan: 2 },
      { id: 'kitchen', col: 2, row: 0, colSpan: 2 },
      { id: 'study', col: 4, row: 0, colSpan: 2 },
      { id: 'bedroom', col: 0, row: 1, colSpan: 3 },
      { id: 'hall', col: 3, row: 1, colSpan: 3 },
    ],
  },
  6: {
    xBands: [BLOCK, BLOCK, BLOCK],
    zBands: [BLOCK, BLOCK],
    zones: [
      { id: 'living', col: 0, row: 0 },
      { id: 'kitchen', col: 1, row: 0 },
      { id: 'study', col: 2, row: 0 },
      { id: 'bedroom', col: 0, row: 1 },
      { id: 'bath', col: 1, row: 1 },
      { id: 'bedroom-main', col: 2, row: 1 },
    ],
  },
  7: {
    xBands: SPLIT_BANDS,
    zBands: [BLOCK, WIDE3, WIDE3],
    zones: [
      { id: 'living', col: 0, row: 0, colSpan: 2 },
      { id: 'kitchen', col: 2, row: 0, colSpan: 2 },
      { id: 'study', col: 4, row: 0, colSpan: 2 },
      { id: 'bedroom', col: 0, row: 1, colSpan: 3 },
      { id: 'bath', col: 3, row: 1, colSpan: 3 },
      { id: 'bedroom-main', col: 0, row: 2, colSpan: 3 },
      { id: 'hall', col: 3, row: 2, colSpan: 3 },
    ],
  },
  8: {
    xBands: SPLIT_BANDS,
    zBands: [BLOCK, BLOCK, WIDE3],
    zones: [
      { id: 'living', col: 0, row: 0, colSpan: 2 },
      { id: 'kitchen', col: 2, row: 0, colSpan: 2 },
      { id: 'study', col: 4, row: 0, colSpan: 2 },
      { id: 'bedroom', col: 0, row: 1, colSpan: 2 },
      { id: 'bath', col: 2, row: 1, colSpan: 2 },
      { id: 'bedroom-main', col: 4, row: 1, colSpan: 2 },
      { id: 'bedroom-guest', col: 0, row: 2, colSpan: 3 },
      { id: 'hall', col: 3, row: 2, colSpan: 3 },
    ],
  },
}

/* --- 문 유도 ---------------------------------------------------------------------- */

/** 격자 위에서 구획이 차지하는 칸 범위(닫힌 구간). 좌표가 아니라 밴드 인덱스다. */
type Block = Readonly<{ c0: number; c1: number; r0: number; r1: number }>

function blockOf(zone: PartyZone): Block {
  return {
    c0: zone.col,
    c1: zone.col + (zone.colSpan ?? 1) - 1,
    r0: zone.row,
    r1: zone.row + (zone.rowSpan ?? 1) - 1,
  }
}

function overlaps(a0: number, a1: number, b0: number, b1: number): boolean {
  return Math.max(a0, b0) <= Math.min(a1, b1)
}

/** 두 구획이 벽 하나를 사이에 두고 맞닿는가. 격자 인덱스로 판단하므로 좌표를 굽기 전에 답이 나온다. */
function isAdjacent(a: Block, b: Block): boolean {
  const sideBySide = (a.c1 + 1 === b.c0 || b.c1 + 1 === a.c0) && overlaps(a.r0, a.r1, b.r0, b.r1)
  const stacked = (a.r1 + 1 === b.r0 || b.r1 + 1 === a.r0) && overlaps(a.c0, a.c1, b.c0, b.c1)
  return sideBySide || stacked
}

/**
 * 맞닿은 구획을 **전부** 잇는다.
 *
 * 최소 신장 트리로 줄이지 않은 이유가 있다. 여럿이 판의 수색은 전원이 동시에 남의 구획을 도는 일이고, 길이 하나뿐이면
 * 그 문이 병목이 되어 "먼저 도착한 사람이 이긴다"가 된다. 벽으로 갈라 두는 것은 시야를 끊기 위한 것이지
 * 돌아가게 만들기 위한 것이 아니다(아파트가 복도를 세 토막 낸 이유와 같은 종류의 결정 - `floorPlans.ts`).
 * 순서는 표의 순서를 따르므로 같은 좌석 수면 문 목록도 항상 같다.
 */
function doorsFor(zones: readonly PartyZone[]): readonly DoorSpec[] {
  const doors: DoorSpec[] = []
  const blocks = zones.map(blockOf)

  for (let i = 0; i < zones.length; i += 1) {
    for (let j = i + 1; j < zones.length; j += 1) {
      const from = zones[i]
      const to = zones[j]
      const a = blocks[i]
      const b = blocks[j]
      if (from === undefined || to === undefined || a === undefined || b === undefined) continue
      if (!isAdjacent(a, b)) continue
      doors.push({ from: from.id, to: to.id, width: PARTY_DOOR_WIDTH_CELLS })
    }
  }

  return doors
}

/* --- 명세 · 굽기 ------------------------------------------------------------------ */

/** 평면도 id. 와이어를 타는 유일한 평면도 정보이므로(`RoomState.templateId`) 형식을 한 함수에 가둔다. */
export function partyPlanId(seatCount: number): string {
  return `party-${String(seatCount)}`
}

/**
 * `party-<n>`에서 좌석 수를 되읽는다. 모양이 아니거나 범위 밖이면 `null` - 모르는 평면도는 세우지 않는다.
 *
 * **정규형만 받는다**(앞자리 0 금지). `party-08`을 8로 읽으면 굽기는 성공하는데 `partyPlanId(8)`은 `party-8`이라
 * `matchPhases.withRoomForSeats`의 `plan.id === templateId` 대조가 매 라운드 거짓이 되어 방을 다시 세운다 -
 * 왕복(`partyPlanId` ↔ 여기)이 규칙이므로 그 왕복을 통과하지 못하는 문자열은 애초에 모르는 평면도다.
 */
export function parsePartyPlanId(planId: string): number | null {
  const match = /^party-([1-9]\d*)$/.exec(planId)
  const raw = match?.[1]
  if (raw === undefined) return null
  const seats = Number(raw)
  return isPartySeatCount(seats) ? seats : null
}

export function isPartySeatCount(seatCount: number): boolean {
  return Number.isInteger(seatCount) && seatCount >= PARTY_SEATS_MIN && seatCount <= PARTY_SEATS_MAX
}

/**
 * 좌석 수를 표가 아는 범위로 접는다.
 *
 * **게임 규칙의 폴백이 아니다.** 구획 수는 언제나 좌석 수와 같다(그것이 M11의 대전제다). 이 함수가 필요한 자리는
 * 판이 아직 시작되지 않은 로비뿐이다 - 매치를 만드는 순간 좌석은 방장 하나뿐이고(`matchState.createMatch`),
 * 그때 세우는 방은 화면에 나가지 않는 채(로비 뷰는 `rooms`를 싣지 않는다) `start`에서 굳은 좌석 수로 다시 세워진다.
 * 그 한 자리를 위해 던지지 않도록 접을 뿐이다.
 *
 * 진행 중에는 접을 일이 없다. 시작 게이트가 하한을 지키고(`matchState.canStart` - 좌석 `PARTY_SEATS_MIN` 미만이면
 * [시작]이 통하지 않는다) 진행 중 퇴장은 좌석을 남기므로(`matchState.onLeave`), 판이 도는 동안 좌석 수는 2~8 안에 있다.
 */
export function clampPartySeats(seatCount: number): number {
  if (!Number.isFinite(seatCount)) return PARTY_SEATS_MIN
  const floored = Math.floor(seatCount)
  return Math.min(Math.max(floored, PARTY_SEATS_MIN), PARTY_SEATS_MAX)
}

/**
 * 좌석 수의 밴드 명세. 굽기 전 단계라 좌표가 없다 - 테스트가 "표와 빌더 중 어느 쪽이 결정론을 깨는가"를
 * 가르기 위해 이 단계를 볼 수 있어야 해서 내보낸다.
 */
export function partyPlanSpec(seatCount: number): BandPlanSpec {
  const layout = LAYOUTS[seatCount]
  if (layout === undefined) {
    throw new RangeError(
      `여럿이 평면도는 ${String(PARTY_SEATS_MIN)}~${String(PARTY_SEATS_MAX)}석만 있다: ${String(seatCount)}석`,
    )
  }

  const zones: readonly ZoneSpec[] = layout.zones.map((zone) => {
    const tag = ZONE_TAGS[zone.id]
    if (tag === undefined) throw new RangeError(`구획 '${zone.id}'의 성격(tag)이 표에 없다`)
    return {
      id: zone.id,
      // 화면에 나가는 이름이 아니라 **키**다(머리말). 사람이 읽는 문구는 `room.<id>`가 사전에서 가져온다.
      name: zone.id,
      tag,
      col: zone.col,
      row: zone.row,
      ...(zone.colSpan === undefined ? {} : { colSpan: zone.colSpan }),
      ...(zone.rowSpan === undefined ? {} : { rowSpan: zone.rowSpan }),
    }
  })

  return { id: partyPlanId(seatCount), xBands: layout.xBands, zBands: layout.zBands, zones, doors: doorsFor(layout.zones) }
}

/**
 * 좌석 수별 평면도 캐시. `floorPlans.getFloorPlan`과 같은 이유다 - 평면도는 불변이고, 셸·조명·생성기·컷어웨이가
 * 서로 다른 인스턴스를 들면 `plan.id` 대조가 참인데도 데이터가 갈릴 여지가 생긴다.
 */
const planCache = new Map<number, FloorPlan>()

/**
 * 좌석 수만큼 구획을 가진 여럿이 평면도. 범위 밖이면 던진다 - 조용히 다른 수의 구획을 돌려주면
 * "내 구획이 없는 사람"이 화면에서야 발견된다.
 */
export function partyPlan(seatCount: number): FloorPlan {
  const cached = planCache.get(seatCount)
  if (cached !== undefined) return cached

  const plan = buildBandPlan(partyPlanSpec(seatCount))
  // 검증은 기존 것 하나뿐이다(머리말). 여기서만 도는 검사를 만들면 그 검사가 언젠가 뒤처진다.
  assertValidFloorPlan(plan)
  planCache.set(seatCount, plan)

  return plan
}

/** 접은 좌석 수로 굽는다(`clampPartySeats`의 이유 참조). 평면도를 고르는 호출부가 쓰는 문이다. */
export function partyPlanForSeats(seatCount: number): FloorPlan {
  return partyPlan(clampPartySeats(seatCount))
}
